import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/ui/Sidebar';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import ContentTypeBadge from '../../../components/ui/ContentTypeBadge';
import ArabicDateTime from '../components/ui/ArabicDateTime';
import Select from '../components/ui/Select';
import Pagination from '../../../components/ui/Pagination';
import adminService from '../../../services/adminService';

const ITEMS_PER_PAGE = 20;

const Archive = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchArchive = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        pageSize: ITEMS_PER_PAGE
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await adminService.getArchive(params);
      setItems(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Failed to fetch archive:', err);
      setError('فشل في تحميل الأرشيف');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchive();
  }, [statusFilter, currentPage]);

  useEffect(() => {
    document.title = 'الأرشيف - Dawayer';
  }, []);

  const getStatusColor = (status) => {
    if (status === 'published') return 'bg-success';
    if (status === 'rejected') return 'bg-error';
    if (status === 'revision') return 'bg-warning';
    return 'bg-muted';
  };

  const getStatusLabel = (status) => {
    if (status === 'published') return 'منشور';
    if (status === 'rejected') return 'مرفوض';
    if (status === 'revision') return 'طلب تعديل';
    return status;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleArticleClick = (item) => {
    if (!item?.id) return;
    const isDiscussion = item?.type?.toLowerCase() === 'discussion';
    navigate(isDiscussion ? `/discussion/${item.id}` : `/article/${item.id}`);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" dir="rtl">
      <Sidebar />
      <main className="lg:mr-60 transition-default">
        <div className="p-6 lg:p-8">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="sm:order-1">
              <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                الأرشيف
              </h1>
              <p className="text-base font-body text-muted-foreground">
                القرارات السابقة
              </p>
            </div>
            <button
              onClick={() => navigate('/homepage')}
              className="flex items-center gap-2 px-5 py-3 rounded-full font-medium text-[15px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_14px_rgba(197,160,89,0.25)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.35)] self-start sm:order-2"
              style={{
                fontFamily: 'Amiri, serif',
                backgroundColor: '#C5A059',
                color: '#FFFFFF'
              }}
            >
              <Icon name="Home" size={18} color="#FFFFFF" />
              <span>الرجوع إلى المنصة</span>
            </button>
          </div>

          <div className="mb-6 w-fit" dir="rtl">
            <Select
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'الكل' },
                { value: 'published', label: 'منشور' },
                { value: 'rejected', label: 'مرفوض' },
                { value: 'revision', label: 'طلب تعديل' }
              ]}
              placeholder="الكل"
              className="min-w-[160px]"
            />
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
              <p className="text-muted-foreground font-body">{error}</p>
              <button onClick={fetchArchive} className="mt-4 text-primary hover:underline font-body">
                إعادة المحاولة
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Archive" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-body text-muted-foreground">
                لا توجد قرارات سابقة
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {items.map((item) => (
                  <div
                    key={item?.id}
                    onClick={() => handleArticleClick(item)}
                    className="flex items-center gap-4 p-4 lg:p-6 rounded-2xl bg-card hover:shadow-warm-md transition-default cursor-pointer"
                  >
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(item?.status)} flex-shrink-0`} />
                    {item?.authorAvatar ? (
                      <Image
                        src={item?.authorAvatar}
                        alt={item?.author}
                        className="w-12 h-12 lg:w-14 lg:h-14 rounded-full object-cover flex-shrink-0"
                        type="user"
                      />
                    ) : (
                      <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Icon name="User" size={24} color="var(--color-primary)" />
                      </div>
                    )}
                    <div className="flex-1 text-right min-w-0">
                      <div className="flex items-center gap-2 mb-1 min-w-0">
                        <ContentTypeBadge type={item?.type} size="sm" className="flex-shrink-0" />
                        <h3 className="text-base lg:text-lg font-semibold text-foreground truncate min-w-0" style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}>
                          {item?.title}
                        </h3>
                      </div>
                      {item?.authorId ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/user/${item.authorId}`);
                          }}
                          className="text-sm font-body text-muted-foreground hover:text-primary transition-colors"
                        >
                          {item?.author}
                        </button>
                      ) : (
                        <p className="text-sm font-body text-muted-foreground">{item?.author}</p>
                      )}
                      {item?.reviewedBy && (
                        <div className="mt-1">
                          {item?.reviewedById ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/user/${item.reviewedById}`);
                              }}
                              className="text-xs font-body text-muted-foreground hover:text-primary transition-colors"
                            >
                              راجعه: {item.reviewedBy}
                            </button>
                          ) : (
                            <p className="text-xs font-body text-muted-foreground">
                              راجعه: {item.reviewedBy}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(item?.status)}`}>
                        {getStatusLabel(item?.status)}
                      </span>
                      {item?.reviewedAt && (
                        <ArabicDateTime
                          date={new Date(item.reviewedAt)}
                          format="full"
                          className="text-sm text-muted-foreground"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalCount > ITEMS_PER_PAGE && (
                <div className="mt-8">
                  <Pagination
                    postsPerPage={ITEMS_PER_PAGE}
                    totalPosts={totalCount}
                    paginate={handlePageChange}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Archive;
